(() => {
  'use strict';

  function money(cents) {
    return (cents / 100).toLocaleString('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 });
  }

  // A 0 price means it hasn't been set in admin yet; mirrors snippets/price.liquid.
  function priceAmount(cents) {
    return cents > 0
      ? `<span class="price__amount">${money(cents)}</span>`
      : '<span class="price__amount price__amount--tbc">Price on request</span>';
  }

  /* ---------------------------------------------------------------------
   * Pause off-screen animations. The doodles, buddies and banners carry
   * dozens of infinite CSS animations; letting them all run (and repaint
   * their SVG filters) while scrolled out of view is the main source of
   * jank on mobile. Sections and closed drawers get .is-offscreen, which
   * freezes every animation inside them until they come back into view.
   * ------------------------------------------------------------------- */
  const offscreenObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-offscreen', !entry.isIntersecting));
      }, { rootMargin: '200px 0px' })
    : null;
  function pauseWhenOffscreen(el) {
    if (offscreenObserver && el) offscreenObserver.observe(el);
  }
  document
    .querySelectorAll('.shopify-section, [data-mobile-nav], [data-search-overlay]')
    .forEach(pauseWhenOffscreen);

  /* ---------------------------------------------------------------------
   * Section Rendering API helpers
   * ------------------------------------------------------------------- */
  function sectionWrapper(id) {
    return document.getElementById('shopify-section-' + id);
  }

  function nearestSectionId(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
  }

  async function refreshSections(ids) {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return;
    const res = await fetch(`${window.location.pathname}?sections=${unique.join(',')}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) return;
    const data = await res.json();
    unique.forEach((id) => {
      const html = data[id];
      const wrapper = sectionWrapper(id);
      if (html && wrapper) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.getElementById('shopify-section-' + id);
        if (next) {
          offscreenObserver?.unobserve(wrapper);
          wrapper.replaceWith(next);
          pauseWhenOffscreen(next);
        }
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Header scroll state
   * ------------------------------------------------------------------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------------
   * Mega menu (hover on desktop)
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
    trigger.addEventListener('mouseenter', () => {
      document.querySelectorAll('.mega-menu').forEach((el) => el.classList.add('is-open'));
    });
  });
  if (header) {
    header.addEventListener('mouseleave', () => {
      document.querySelectorAll('.mega-menu').forEach((el) => el.classList.remove('is-open'));
    });
  }

  /* ---------------------------------------------------------------------
   * Mobile nav
   * ------------------------------------------------------------------- */
  const mobileNav = document.querySelector('[data-mobile-nav]');
  function toggleMobileNav(open) {
    if (mobileNav) mobileNav.classList.toggle('is-open', open);
  }
  document.querySelectorAll('[data-open-mobile-nav]').forEach((btn) =>
    btn.addEventListener('click', () => toggleMobileNav(true))
  );
  document.querySelectorAll('[data-close-mobile-nav]').forEach((btn) =>
    btn.addEventListener('click', () => toggleMobileNav(false))
  );
  mobileNav?.addEventListener('click', (e) => {
    if (e.target === mobileNav) toggleMobileNav(false);
  });

  document.querySelectorAll('[data-mobile-nav-toggle]').forEach((toggle) => {
    const submenu = toggle.parentElement.querySelector('[data-mobile-nav-submenu]');
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      if (submenu) submenu.hidden = isOpen;
    });
  });

  /* ---------------------------------------------------------------------
   * Search overlay
   * ------------------------------------------------------------------- */
  const searchOverlay = document.querySelector('[data-search-overlay]');
  function toggleSearch(open) {
    if (!searchOverlay) return;
    searchOverlay.classList.toggle('is-open', open);
    if (open) {
      requestAnimationFrame(() => searchOverlay.querySelector('[data-search-input]')?.focus());
    } else {
      const input = searchOverlay.querySelector('[data-search-input]');
      const results = searchOverlay.querySelector('[data-search-results]');
      const trending = searchOverlay.querySelector('[data-search-trending]');
      if (input) input.value = '';
      if (results) {
        results.hidden = true;
        results.innerHTML = '';
      }
      if (trending) trending.hidden = false;
    }
  }
  document.querySelectorAll('[data-open-search]').forEach((btn) =>
    btn.addEventListener('click', () => toggleSearch(true))
  );
  document.querySelectorAll('[data-close-search]').forEach((btn) =>
    btn.addEventListener('click', () => toggleSearch(false))
  );
  searchOverlay?.addEventListener('click', (e) => {
    if (e.target === searchOverlay || e.target.classList.contains('search-overlay__panel')) toggleSearch(false);
  });

  /* ---------------------------------------------------------------------
   * Predictive search: live results as you type, via Shopify's
   * Predictive Search AJAX API (/search/suggest.json).
   * ------------------------------------------------------------------- */
  const searchInput = searchOverlay?.querySelector('[data-search-input]');
  const searchResults = searchOverlay?.querySelector('[data-search-results]');
  const searchTrending = searchOverlay?.querySelector('[data-search-trending]');
  const searchForm = searchInput?.closest('form');

  if (searchInput && searchResults && searchForm) {
    let debounceTimer;
    let latestQuery = '';

    function resetToTrending() {
      searchResults.hidden = true;
      searchResults.innerHTML = '';
      if (searchTrending) searchTrending.hidden = false;
    }

    function renderResults(query, resources) {
      const products = resources.results.products || [];
      const queries = resources.results.queries || [];

      if (products.length === 0 && queries.length === 0) {
        searchResults.innerHTML = `<p class="search-overlay__empty">No results for &ldquo;${query}&rdquo;.</p>`;
        searchResults.hidden = false;
        if (searchTrending) searchTrending.hidden = true;
        return;
      }

      const suggestionsHtml = queries
        .map((q) => `<a class="search-overlay__suggestion" href="${q.url}">${q.styled_text}</a>`)
        .join('');

      const productsHtml = products
        .map((product) => `
          <a class="search-overlay__result" href="${product.url}">
            <span class="search-overlay__result-media">
              ${product.image ? `<img src="${product.image}" alt="" loading="lazy" width="56" height="56">` : ''}
            </span>
            <span class="search-overlay__result-info">
              <span class="search-overlay__result-title">${product.title}</span>
              <span class="price">${priceAmount(Math.round(parseFloat(product.price) * 100))}</span>
            </span>
          </a>
        `)
        .join('');

      searchResults.innerHTML = `
        ${suggestionsHtml ? `<div class="search-overlay__suggestions">${suggestionsHtml}</div>` : ''}
        ${productsHtml ? `<div class="search-overlay__results-grid">${productsHtml}</div>` : ''}
        <a class="search-overlay__view-all" href="${searchForm.action}?q=${encodeURIComponent(query)}&type=product">
          View all results for &ldquo;${query}&rdquo;
        </a>
      `;
      searchResults.hidden = false;
      if (searchTrending) searchTrending.hidden = true;
    }

    async function runSearch(query) {
      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,query&resources[limit]=6&resources[options][unavailable_products]=last`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok || query !== latestQuery) return;
      const data = await res.json();
      if (query !== latestQuery) return;
      renderResults(query, data.resources);
    }

    searchInput.addEventListener('input', () => {
      const query = searchInput.value.trim();
      latestQuery = query;
      clearTimeout(debounceTimer);

      if (query.length < 2) {
        resetToTrending();
        return;
      }

      debounceTimer = setTimeout(() => runSearch(query), 250);
    });
  }

  /* ---------------------------------------------------------------------
   * Cart drawer open/close
   * ------------------------------------------------------------------- */
  function cartDrawerBackdrop() {
    return document.querySelector('[data-cart-drawer]');
  }
  function toggleCart(open) {
    const backdrop = cartDrawerBackdrop();
    backdrop?.classList.toggle('is-open', open);
  }
  document.body.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart]')) toggleCart(true);
    if (e.target.closest('[data-close-cart]')) toggleCart(false);
  });
  document.body.addEventListener('click', (e) => {
    const backdrop = cartDrawerBackdrop();
    if (backdrop && e.target === backdrop) toggleCart(false);
  });

  /* ---------------------------------------------------------------------
   * Escape key closes any open overlay
   * ------------------------------------------------------------------- */
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    toggleMobileNav(false);
    toggleSearch(false);
    toggleCart(false);
  });

  /* ---------------------------------------------------------------------
   * Cart mutations (quick add, qty +/-, remove) — AJAX via Cart API +
   * Section Rendering API, works from the drawer or the /cart page.
   * ------------------------------------------------------------------- */
  function updateCartCountBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  async function addToCart(id, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) return;
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    updateCartCountBadge(cart.item_count);

    // Deliberately does NOT refresh the section the trigger lives in (unlike
    // changeCartLine below). Doing so would replace the live product form —
    // including the variant swatches, qty stepper and this very submit
    // listener — with a fresh, unbound copy from the server, so the very
    // next click would fall through to a native (non-AJAX) form submission
    // straight to /cart instead of opening the drawer.
    await refreshSections(['cart-drawer', 'header']);
    toggleCart(true);
  }

  async function changeCartLine(line, quantity, triggerEl) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) return;
    const cart = await res.json();
    updateCartCountBadge(cart.item_count);

    const extraSectionId = triggerEl ? nearestSectionId(triggerEl) : null;
    await refreshSections(['cart-drawer', 'header', extraSectionId]);
  }

  document.body.addEventListener('click', (e) => {
    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      addToCart(quickAdd.dataset.variantId, 1);
      return;
    }

    const increase = e.target.closest('[data-cart-qty-increase]');
    if (increase) {
      const line = Number(increase.dataset.line);
      const currentQtyEl = increase.parentElement.querySelector('span');
      const nextQty = (Number(currentQtyEl?.textContent) || 1) + 1;
      changeCartLine(line, nextQty, increase);
      return;
    }

    const decrease = e.target.closest('[data-cart-qty-decrease]');
    if (decrease) {
      const line = Number(decrease.dataset.line);
      const currentQtyEl = decrease.parentElement.querySelector('span');
      const nextQty = Math.max(0, (Number(currentQtyEl?.textContent) || 1) - 1);
      changeCartLine(line, nextQty, decrease);
      return;
    }

    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      const line = Number(remove.dataset.line);
      changeCartLine(line, 0, remove);
    }
  });

  /* ---------------------------------------------------------------------
   * Product page: variant swatches, quantity stepper, AJAX add to cart
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const root = form.closest('[data-product-root]') || document;
    const productDataEl = root.querySelector('[data-product-json]');
    const product = productDataEl ? JSON.parse(productDataEl.textContent) : null;
    const hiddenIdInput = form.querySelector('[data-selected-variant-id]');
    const priceWrap = root.querySelector('[data-product-price]');
    const addButton = form.querySelector('[data-add-to-cart]');
    const qtyInput = form.querySelector('[data-qty-input]');

    function findVariant() {
      if (!product) return null;
      const selected = {};
      form.querySelectorAll('[data-option-index].is-selected').forEach((btn) => {
        selected[btn.dataset.optionIndex] = btn.dataset.optionValue;
      });
      return (
        product.variants.find((variant) =>
          Object.entries(selected).every(([index, value]) => variant[`option${index}`] === value)
        ) || null
      );
    }

    function applyVariant(variant) {
      if (!variant) return;
      if (hiddenIdInput) hiddenIdInput.value = variant.id;
      // Lets the product gallery jump to this variant's image.
      root.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variant }, bubbles: true }));
      if (priceWrap) {
        priceWrap.innerHTML = `<span class="price">${priceAmount(variant.price)}${
          variant.price > 0 && variant.compare_at_price > variant.price
            ? `<span class="price__compare">${money(variant.compare_at_price)}</span>`
            : ''
        }</span>`;
      }
      if (addButton) {
        const priced = variant.price > 0;
        addButton.disabled = !variant.available || !priced;
        addButton.textContent = !priced
          ? 'Price on request'
          : variant.available ? addButton.dataset.availableLabel || 'Add to Cart' : 'Out of Stock';
      }
    }

    if (addButton && !addButton.dataset.availableLabel) {
      addButton.dataset.availableLabel = addButton.textContent.trim();
    }

    form.querySelectorAll('[data-option-index]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const group = swatch.closest('fieldset');
        group.querySelectorAll('[data-option-index]').forEach((el) => el.classList.remove('is-selected'));
        swatch.classList.add('is-selected');
        const labelEl = group.querySelector('[data-option-value]');
        if (labelEl && labelEl.tagName !== 'BUTTON') labelEl.textContent = swatch.dataset.optionValue;
        applyVariant(findVariant());
      });
    });

    qtyInput?.parentElement.querySelector('[data-qty-decrease]')?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, Number(qtyInput.value) - 1);
    });
    qtyInput?.parentElement.querySelector('[data-qty-increase]')?.addEventListener('click', () => {
      qtyInput.value = Number(qtyInput.value) + 1;
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!hiddenIdInput?.value) return;
      addToCart(hiddenIdInput.value, Number(qtyInput?.value) || 1);
    });
  });

  /* ---------------------------------------------------------------------
   * Product gallery: swipe/thumbnail track, hover magnifier on desktop and
   * a full-screen viewer with pinch / double-tap / wheel / +- zoom and
   * drag-to-pan. Markup: snippets/product-gallery.liquid
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const thumbsWrap = gallery.querySelector('.pgallery__thumbs');
    const currentEl = gallery.querySelector('[data-gallery-current]');
    const lightbox = gallery.parentElement.querySelector('[data-lightbox]');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let index = 0;

    // A fixed overlay inside the sticky media column would sit under the
    // header's stacking context, so the viewer lives on <body>.
    if (lightbox) document.body.appendChild(lightbox);

    function setActive(i) {
      index = i;
      thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      if (currentEl) currentEl.textContent = i + 1;
      const thumb = thumbs[i];
      if (thumb && thumbsWrap) {
        const left = thumb.offsetLeft - (thumbsWrap.clientWidth - thumb.clientWidth) / 2;
        thumbsWrap.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    function goTo(i, smooth = true) {
      const n = slides.length;
      const next = ((i % n) + n) % n;
      track.scrollTo({ left: slides[next].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      setActive(next);
    }

    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index && slides[i]) setActive(i);
      }, 60);
    }, { passive: true });

    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(index - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(index + 1));
    thumbs.forEach((t, k) => t.addEventListener('click', () => goTo(k)));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
    });

    // Start on the selected variant's image, and follow variant changes.
    const mediaIndex = (id) => slides.findIndex((s) => s.dataset.mediaId === String(id));
    const initial = mediaIndex(gallery.dataset.initialMedia);
    if (initial > 0) requestAnimationFrame(() => goTo(initial, false));
    (gallery.closest('[data-product-root]') || document).addEventListener('product:variant-change', (e) => {
      const media = e.detail?.variant?.featured_media;
      const i = media ? mediaIndex(media.id) : -1;
      if (i >= 0 && i !== index) goTo(i);
    });

    /* Desktop hover magnifier: a lens layer shows the 2400px image,
       positioned under the cursor. */
    if (finePointer) {
      slides.forEach((slide) => {
        const area = slide.querySelector('.pgallery__zoom-area');
        const lens = document.createElement('span');
        lens.className = 'pgallery__lens';
        lens.setAttribute('aria-hidden', 'true');
        area.appendChild(lens);
        let loaded = false;
        area.addEventListener('mouseenter', () => {
          if (!loaded) { lens.style.backgroundImage = `url("${slide.dataset.zoomSrc}")`; loaded = true; }
          slide.classList.add('is-zooming');
        });
        area.addEventListener('mousemove', (e) => {
          const r = area.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          lens.style.backgroundPosition = `${x}% ${y}%`;
        });
        area.addEventListener('mouseleave', () => slide.classList.remove('is-zooming'));
      });
    }

    if (!lightbox) return;

    /* ---------------- Full-screen viewer ---------------- */
    const stage = lightbox.querySelector('[data-lightbox-stage]');
    const img = lightbox.querySelector('[data-lightbox-img]');
    const lbCurrent = lightbox.querySelector('[data-lightbox-current]');
    const hint = lightbox.querySelector('[data-lightbox-hint]');
    const closeBtn = lightbox.querySelector('[data-lightbox-close]');
    const MIN = 1, MAX = 4, STEP = 2.5;
    let lbIndex = 0, scale = 1, tx = 0, ty = 0, lastFocus = null;

    const render = (animate) => {
      img.classList.toggle('is-animating', !!animate);
      img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
      lightbox.classList.toggle('is-zoomed', scale > 1.01);
    };
    function clampPan() {
      const w = img.offsetWidth * scale, h = img.offsetHeight * scale;
      const maxX = Math.max(0, (w - stage.clientWidth) / 2);
      const maxY = Math.max(0, (h - stage.clientHeight) / 2);
      tx = Math.min(maxX, Math.max(-maxX, tx));
      ty = Math.min(maxY, Math.max(-maxY, ty));
    }
    // Zoom to `next`, keeping the point (px, py) — relative to the stage centre — fixed.
    function zoomTo(next, px = 0, py = 0, animate = true) {
      next = Math.min(MAX, Math.max(MIN, next));
      tx = px - (px - tx) * (next / scale);
      ty = py - (py - ty) * (next / scale);
      scale = next;
      if (scale === MIN) { tx = 0; ty = 0; }
      clampPan();
      render(animate);
    }
    const fromCentre = (clientX, clientY) => {
      const r = stage.getBoundingClientRect();
      return [clientX - r.left - r.width / 2, clientY - r.top - r.height / 2];
    };

    function show(i) {
      const n = slides.length;
      lbIndex = ((i % n) + n) % n;
      const slide = slides[lbIndex];
      scale = 1; tx = 0; ty = 0; render(false);
      lightbox.classList.add('is-loading');
      img.onload = () => lightbox.classList.remove('is-loading');
      img.width = Number(slide.dataset.zoomWidth);
      img.height = Number(slide.dataset.zoomHeight);
      img.src = slide.dataset.zoomSrc;
      img.alt = slide.querySelector('img')?.alt || '';
      if (lbCurrent) lbCurrent.textContent = lbIndex + 1;
    }
    function open(i) {
      lastFocus = document.activeElement;
      lightbox.hidden = false;
      document.documentElement.classList.add('has-lightbox');
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      show(i);
      closeBtn?.focus();
    }
    function close() {
      lightbox.classList.remove('is-open');
      document.documentElement.classList.remove('has-lightbox');
      setTimeout(() => { lightbox.hidden = true; }, reduceMotion ? 0 : 200);
      if (lbIndex !== index) goTo(lbIndex, false);
      lastFocus?.focus?.();
    }

    gallery.querySelectorAll('[data-gallery-open]').forEach((btn) => {
      btn.addEventListener('click', () => open(btn.closest('[data-gallery-slide]') ? slides.indexOf(btn.closest('[data-gallery-slide]')) : index));
    });
    closeBtn?.addEventListener('click', close);
    lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => show(lbIndex - 1));
    lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => show(lbIndex + 1));
    lightbox.querySelector('[data-lightbox-zoom-in]')?.addEventListener('click', () => zoomTo(scale * 1.5));
    lightbox.querySelector('[data-lightbox-zoom-out]')?.addEventListener('click', () => zoomTo(scale / 1.5));

    document.addEventListener('keydown', (e) => {
      if (lightbox.hidden || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight' && scale === 1) show(lbIndex + 1);
      else if (e.key === 'ArrowLeft' && scale === 1) show(lbIndex - 1);
      else if (e.key === '+' || e.key === '=') zoomTo(scale * 1.5);
      else if (e.key === '-') zoomTo(scale / 1.5);
      else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        const f = [...lightbox.querySelectorAll('button:not([hidden])')];
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    stage.addEventListener('wheel', (e) => {
      e.preventDefault();
      const [px, py] = fromCentre(e.clientX, e.clientY);
      zoomTo(scale * (e.deltaY < 0 ? 1.2 : 1 / 1.2), px, py, false);
    }, { passive: false });

    /* Pointer gestures: 1 finger pans (or swipes when not zoomed),
       2 fingers pinch, a double tap / single click toggles zoom. */
    const pointers = new Map();
    let start = null, pinch = null, lastTap = 0, lastTapXY = [0, 0];
    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, [e.clientX, e.clientY]);
      hint?.classList.add('is-hidden');
      if (pointers.size === 1) {
        start = { x: e.clientX, y: e.clientY, tx, ty, moved: false, t: Date.now() };
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { dist: Math.hypot(a[0] - b[0], a[1] - b[1]), scale, mid: fromCentre((a[0] + b[0]) / 2, (a[1] + b[1]) / 2), tx, ty };
        start = null;
      }
    });
    stage.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, [e.clientX, e.clientY]);
      if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const next = Math.min(MAX, Math.max(MIN, pinch.scale * (Math.hypot(a[0] - b[0], a[1] - b[1]) / pinch.dist)));
        const [px, py] = pinch.mid;
        tx = px - (px - pinch.tx) * (next / pinch.scale);
        ty = py - (py - pinch.ty) * (next / pinch.scale);
        scale = next;
        clampPan();
        render(false);
      } else if (start) {
        const dx = e.clientX - start.x, dy = e.clientY - start.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) start.moved = true;
        if (scale > 1) { tx = start.tx + dx; ty = start.ty + dy; clampPan(); render(false); }
        else if (slides.length > 1) { tx = dx; render(false); }
      }
    });
    const endPointer = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pinch) { if (pointers.size < 2) { pinch = null; if (scale <= 1.01) zoomTo(1); } return; }
      if (!start) return;
      const dx = e.clientX - start.x;
      if (start.moved) {
        if (scale === 1) {
          if (Math.abs(dx) > 60 && slides.length > 1) show(lbIndex + (dx < 0 ? 1 : -1));
          else { tx = 0; render(true); }
        }
      } else if (e.pointerType === 'mouse') {
        const [px, py] = fromCentre(e.clientX, e.clientY);
        zoomTo(scale > 1 ? 1 : STEP, px, py);
      } else {
        const now = Date.now();
        const near = Math.hypot(e.clientX - lastTapXY[0], e.clientY - lastTapXY[1]) < 30;
        if (now - lastTap < 300 && near) {
          const [px, py] = fromCentre(e.clientX, e.clientY);
          zoomTo(scale > 1 ? 1 : STEP, px, py);
          lastTap = 0;
        } else { lastTap = now; lastTapXY = [e.clientX, e.clientY]; }
      }
      start = null;
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);
    window.addEventListener('resize', () => { if (!lightbox.hidden) { clampPan(); render(false); } });
  });

  /* ---------------------------------------------------------------------
   * Blob carousel: native scroll-snap track (so touch swipe just works),
   * arrows scroll it, and a progress bar + disabled arrow states follow the
   * scroll position.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-blob-carousel]').forEach((root) => {
    const track = root.querySelector('[data-carousel-track]');
    const slides = [...root.querySelectorAll('[data-carousel-slide]')];
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const progress = root.querySelector('[data-carousel-progress]');
    if (!track || slides.length < 2) return;

    let index = 0;

    function sync() {
      const current = Math.round(track.scrollLeft / track.clientWidth);
      index = Math.min(slides.length - 1, Math.max(0, current));
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.toggleAttribute('aria-hidden', !active);
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      if (progress) progress.style.width = `${((index + 1) / slides.length) * 100}%`;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    function go(i) {
      track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
    }

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { sync(); ticking = false; });
    }, { passive: true });
    window.addEventListener('resize', () => go(index));

    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    sync();
  });

  /* ---------------------------------------------------------------------
   * Optional 3D hero: loaded lazily, only when the merchant opts in via
   * theme settings, and only once the browser is idle so it never
   * competes with the critical rendering path.
   * ------------------------------------------------------------------- */
  const heroMount = document.querySelector('[data-hero-3d-mount]');
  const hero3dSrc = heroMount?.dataset.hero3dSrc;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (heroMount && hero3dSrc && !prefersReducedMotion) {
    // Loaded as soon as this script runs — a dynamic import() is already
    // off the critical rendering path, so there's no reason to add extra
    // (and previously unbounded, up to 4s) idle/load delay on top of it.
    import(/* webpackIgnore: true */ hero3dSrc).catch((err) =>
      console.error('[theme] failed to load hero-3d.js:', err)
    );
  } else if (heroMount && prefersReducedMotion) {
    console.info('[theme] 3D hero skipped: prefers-reduced-motion is enabled for this browser/OS.');
  }

  /* ---------------------------------------------------------------------
   * Bahadur Bacha tabs ("Join the Club" / "Free Coloring Pages").
   * The URL hash picks the tab, so menu links like
   * /pages/bahadur-bacha#coloring-pages open the right one, and in-page
   * links such as #join or #faq switch back to the panel that holds them.
   * A tab with data-bb-scroll also scrolls to that element (the club tab
   * jumps to the sign-up form, since its panel is usually already open).
   * ------------------------------------------------------------------- */
  const bbTabs = document.querySelector('[data-bb-tabs]');
  if (bbTabs) {
    const tabs = [...bbTabs.querySelectorAll('[data-bb-tab]')];
    const panels = [...document.querySelectorAll('[data-bb-panel]')];

    const activate = (id, focusTab) => {
      tabs.forEach((tab) => {
        const on = tab.dataset.bbTab === id;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on && focusTab) tab.focus();
      });
      panels.forEach((panel) => { panel.hidden = panel.id !== id; });
    };

    const fromHash = () => {
      const hash = decodeURIComponent(location.hash.slice(1));
      if (!hash) return false;
      const target = document.getElementById(hash);
      const panel = target && (target.matches('[data-bb-panel]') ? target : target.closest('[data-bb-panel]'));
      if (!panel) return false;
      activate(panel.id);
      if (target !== panel) target.scrollIntoView();
      return true;
    };

    if (!fromHash()) activate(tabs[0].dataset.bbTab);
    window.addEventListener('hashchange', fromHash);

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activate(tab.dataset.bbTab);
        history.replaceState(null, '', '#' + tab.dataset.bbTab);
        document.getElementById(tab.dataset.bbScroll || '')?.scrollIntoView({ block: 'start' });
      });
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        activate(next.dataset.bbTab, true);
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Event gallery: category filter chips + full-screen photo/video viewer.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const items = [...gallery.querySelectorAll('[data-gallery-item]')];
    const chips = [...gallery.querySelectorAll('[data-gallery-filter]')];
    const empty = gallery.querySelector('[data-gallery-empty]');
    const viewer = gallery.querySelector('[data-gallery-viewer]');
    if (!viewer) return;
    const media = viewer.querySelector('[data-gallery-media]');
    const caption = viewer.querySelector('[data-gallery-caption]');
    let current = -1;
    let lastFocus = null;

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const filter = chip.dataset.galleryFilter;
        chips.forEach((c) => {
          const on = c === chip;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        let shown = 0;
        items.forEach((item) => {
          const match = filter === 'all' || item.dataset.tags.split(' ').includes(filter);
          item.classList.toggle('is-hidden', !match);
          if (match) shown += 1;
        });
        if (empty) empty.hidden = shown > 0;
      });
    });

    const visible = () => items.filter((item) => !item.classList.contains('is-hidden'));

    const show = (index) => {
      const list = visible();
      if (!list.length) return;
      current = (index + list.length) % list.length;
      const trigger = list[current].querySelector('[data-gallery-open]');
      const { type, src } = trigger.dataset;
      media.innerHTML = '';
      const el = document.createElement(type === 'video' ? 'video' : 'img');
      el.src = src;
      if (type === 'video') {
        el.controls = true;
        el.autoplay = true;
        el.playsInline = true;
      } else {
        el.alt = trigger.dataset.caption || '';
      }
      media.appendChild(el);
      caption.textContent = trigger.dataset.caption || '';
    };

    const open = (trigger) => {
      lastFocus = trigger;
      viewer.hidden = false;
      document.documentElement.classList.add('has-gallery-viewer');
      show(visible().indexOf(trigger.closest('[data-gallery-item]')));
      viewer.querySelector('[data-gallery-close]').focus();
    };

    const close = () => {
      viewer.hidden = true;
      media.innerHTML = '';
      document.documentElement.classList.remove('has-gallery-viewer');
      lastFocus?.focus();
    };

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-gallery-open]');
      if (trigger) open(trigger);
    });
    viewer.querySelector('[data-gallery-close]').addEventListener('click', close);
    viewer.querySelector('[data-gallery-prev]').addEventListener('click', () => show(current - 1));
    viewer.querySelector('[data-gallery-next]').addEventListener('click', () => show(current + 1));
    viewer.addEventListener('click', (event) => { if (event.target === viewer) close(); });
    document.addEventListener('keydown', (event) => {
      if (viewer.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    });

    let touchX = null;
    viewer.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
    viewer.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const dx = event.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 50) show(current + (dx < 0 ? 1 : -1));
      touchX = null;
    });
  });

  /* ---------------------------------------------------------------------
   * Bahadur Bacha sign-ups: keep the parent's answers if Shopify rejects the
   * submission. Shopify re-renders the page with empty fields on an error,
   * so answers are saved for this tab on submit, restored when the error
   * box is shown and cleared once the form posts successfully.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-bacha-form]').forEach((form) => {
    const storeKey = `bacha-form:${form.id}`;
    const errors = form.querySelector('[data-bacha-errors]');
    const storage = {
      get: () => { try { return JSON.parse(sessionStorage.getItem(storeKey) || 'null'); } catch (err) { return null; } },
      set: (value) => { try { sessionStorage.setItem(storeKey, JSON.stringify(value)); } catch (err) { /* private mode */ } },
      clear: () => { try { sessionStorage.removeItem(storeKey); } catch (err) { /* private mode */ } },
    };

    if (!errors) {
      // Success state (or a fresh visit): nothing to restore.
      if (form.querySelector('.form-success')) storage.clear();
    } else {
      const saved = storage.get();
      if (saved) {
        [...form.elements].forEach((el) => {
          if (!el.name || el.type === 'hidden' || !(el.name in saved)) return;
          if (el.type === 'checkbox') el.checked = saved[el.name];
          else el.value = saved[el.name];
        });
      }
      errors.focus({ preventScroll: true });
    }

    form.addEventListener('submit', () => {
      const values = {};
      [...form.elements].forEach((el) => {
        if (!el.name || el.type === 'hidden') return;
        values[el.name] = el.type === 'checkbox' ? el.checked : el.value;
      });
      storage.set(values);
    });
  });

  /* ---------------------------------------------------------------------
   * Bahadur Bacha sign-ups → Klaviyo (optional). The Shopify contact form
   * still submits normally (that's the email notification); when the
   * section has a Klaviyo public key + list ID, we also subscribe the parent
   * with the child's birthday so a Klaviyo date-property flow can send the
   * yearly birthday email/SMS. keepalive lets the request finish while the
   * page navigates to the form's thank-you state.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-bacha-form]').forEach((form) => {
    const key = form.dataset.klaviyoKey;
    const list = form.dataset.klaviyoList;
    if (!key || !list) return;
    form.addEventListener('submit', () => {
      const val = (name) => (form.elements[`contact[${name}]`]?.value || '').trim();
      const dob = val('Child date of birth'); // YYYY-MM-DD from <input type="date">
      const [, month, day] = dob.split('-');
      const optIn = form.querySelector('[data-bacha-birthday-optin]')?.checked || false;
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Bahadur Bacha club form',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: val('email'),
                  first_name: val('name'),
                  location: { city: val('City'), country: 'Pakistan' },
                  properties: {
                    child_name: val('Child name'),
                    child_birthday: dob,
                    child_birthday_month: Number(month) || null,
                    child_birthday_day: Number(day) || null,
                    school: val('School'),
                    whatsapp: val('phone'),
                    postal_address: val('Postal address'),
                    birthday_wishes_opt_in: optIn,
                    bahadur_bacha_member: true,
                  },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      };
      try {
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify(body),
        }).catch((err) => console.warn('[bahadur-bacha] Klaviyo sync failed:', err));
      } catch (err) {
        console.warn('[bahadur-bacha] Klaviyo sync failed:', err);
      }
    });
  });

  /* Print a single coloring sheet without the rest of the page. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-print-sheet]');
    if (!button) return;
    const win = window.open('', '_blank');
    if (!win) {
      window.open(button.dataset.printSheet, '_blank');
      return;
    }
    const title = (button.dataset.printTitle || 'Coloring page').replace(/[<>&"]/g, '');
    win.document.write(
      `<!doctype html><title>${title}</title>` +
      '<style>@page{size:A4 portrait;margin:8mm}html,body{margin:0;height:100%}' +
      'body{display:flex;align-items:center;justify-content:center}' +
      'img{max-width:100%;max-height:100vh;object-fit:contain}</style>' +
      `<img src="${new URL(button.dataset.printSheet, location.href).href.replace(/"/g, '%22')}" alt="">`
    );
    win.document.close();
    const img = win.document.querySelector('img');
    const go = () => { win.focus(); win.print(); };
    if (img.complete) go(); else img.addEventListener('load', go);
  });
})();
